import { ReceiveShipmentsService } from '@/api';
import { ProcessesService, ProcessStatus } from '@/api/process';
import { StockOnHandAlignmentService } from '@/api/receive/services/StockOnHandAlignmentService';

import { useAppDispatch } from '@/app/store';
import { MQTTContext } from '@/context/MQTT';
import { initBISState, setProcessLocked as setBISProcessLocked, } from '@/features/bis/backInStockSlice';
import { initCycleCountState } from '@/features/cycleCount/cycleCountSlice';

import { initDeliveryList, unlockDeliveries, } from '@/features/deliveries/deliveriesSlice';
import { initManageDeliveryState } from '@/features/deliveriesManage/deliveriesManageSlice';
import { deleteTags, initDevices, removeDeviceInUse, } from '@/features/devices/devicesSlice';
import { initExpiringSessionState } from '@/features/expiringSession/expiringSession';
import { initInventoryState } from '@/features/inventory/inventorySlice';
import { initPosState } from '@/features/pos/posSlice';
import { initPrints } from '@/features/print/printSlice';
import { initConfirmDeliveryProducts } from '@/features/products/confirmDeliveryProductsSlice';
import { initDeliveryProducts } from '@/features/products/deliveryProductsSlice';
import { initScannedProducts } from '@/features/products/scannedProductsSlice';
import { initProfilingReasonCode } from '@/features/profiling/reasonCodeSlice';
import { initRolesState } from '@/features/profiling/rolesSlice';
import { initProfilingStoresState } from '@/features/profiling/storesSlice';
import { initProfilingUserState } from '@/features/profiling/usersSlice';
import { updateProcessStatus } from '@/features/processStatus/processStatusSlice';
import { changeRecallStatus, initRecallState, } from '@/features/recall/recallSlice';
import { initReprice } from '@/features/repricing/repriceSlice';
import { initStoreToStoreState } from '@/features/s2s/storeToStoreSlice';
import { initSaleProducts } from '@/features/sales/salesProductsSlice';
import { initDamagesReturnsState } from '@/features/storeDamages/storeDamagesSlice';
import { ScanDevice } from '@/types/device';
import React from 'react';
import { useSubscribeDeviceStatus } from './mqtt/useSubscribeDeviceStatus';
import useHandleProcess from './useHandleProcess';
import { useSelector } from './useSelector';

type InitApplicationState = () => Promise<void>;

export const useInitApplication = (): InitApplicationState => {
  const dispatch = useAppDispatch();
  const { setDeviceIdOnMqttClient } = React.useContext(MQTTContext);
  const { username } = useSelector(state => state.user);
  const storeCode = useSelector(
    state => state.currentStore.store?.storeCode || ''
  );
  const subscribeDevice = useSubscribeDeviceStatus();
  const bisIsLocked = useSelector(state => state.bis.processLocked);
  const salesPosProcessIsLocked = useSelector(state => state.pos.processLocked);
  const manualProcessLocked = useSelector(
    state => state.loader.manualProcessLocked
  );
  const deleteProcessLocked = useSelector(
    state => state.loader.deleteProcessLocked
  );
  const returnPosProcessIsLocked = useSelector(
    state => state.returnPos.processLocked
  );
  const cycleCountLocked = useSelector(
    state => state.cycleCount.cyclecCountLocked
  );
  const sohaLocked = useSelector(state => state.cycleCount.sohLocked);

  const { recall } = useSelector(state => state.recall);
  const { deliveryManagedInUse } = useSelector(state => state.manageDeliveries);

  const handleProcess = useHandleProcess();

  const initApplicationState = React.useCallback(async () => {
    const storage = sessionStorage.getItem('deviceInUse');

    const deviceInStorage = storage
      ? (JSON.parse(storage) as Required<ScanDevice>)
      : undefined;

    if (deviceInStorage) {
      sessionStorage.removeItem('deviceInUse');
      // dispatch(addDeviceInUse(deviceInStorage));
      // await dispatch(
      //   updateDeviceStatus({
      //     deviceId: deviceInStorage.deviceId,
      //     nameDevice: deviceInStorage.nameDevice,
      //     statusMode: 'no mode',
      //     storeId: deviceInStorage.storeId,
      //     statusDevice: DeviceStatus.ONLINE,
      //   })
      // );

      await dispatch(deleteTags());
      subscribeDevice.publish(deviceInStorage.deviceId, undefined);
      setDeviceIdOnMqttClient(undefined);
      dispatch(removeDeviceInUse());
    }

    if (recall.status !== 'Completed') {
      await dispatch(changeRecallStatus());
    }

    if (deliveryManagedInUse) {
      await ReceiveShipmentsService.receiveshipChangeDeliveryStatus({
        requestBody: {
          deliveryNumbers: [deliveryManagedInUse],
          status: 'New',
        },
      });
    }

    if (bisIsLocked) {
      await handleProcess({ process: 'BIS', isLocked: false });
      dispatch(setBISProcessLocked(false));
    }

    if (salesPosProcessIsLocked) {
      await handleProcess({ process: 'SLEX', isLocked: false });
    }

    if (returnPosProcessIsLocked) {
      await handleProcess({ process: 'RTPS', isLocked: false });
    }

    if (manualProcessLocked) {
      await handleProcess({ process: 'MSUP', isLocked: false });
    }

    if (deleteProcessLocked) {
      await handleProcess({ process: 'DSUP', isLocked: false });
    }

    if (cycleCountLocked) {
      await handleProcess({ process: 'CYCO', isLocked: false });
    }

    const sohaResponse = await StockOnHandAlignmentService.stockonhandRecapSohAlignment({
      storeCode,
    });

    const sohaOngoing =
      sohaResponse?.stockOnHandItemsList?.some(
        ({ stockOnHandStatus }) => stockOnHandStatus === 'ONGOING'
      ) ?? false;

    if (sohaLocked && !sohaOngoing) {
      // se non c'è più nessuna SOHA in corso, rilascia il lock
        await handleProcess({ process: 'SOHA', isLocked: false });
    } else if (!sohaLocked && sohaOngoing) {
      // c'è una SOHA in corso ma da noi risulta sbloccato: verifica il vero owner
      const processStatus: ProcessStatus[] =
        (await ProcessesService.readProcessStatus({
          storeCode: storeCode || '',
          filterByStoreCode: true,
        })) || [];

      const sohAlignment = processStatus.find(
        ({ process }) => process === 'SOHA'
      );

      if (sohAlignment?.lockedBy !== username) {
        await handleProcess({
          process: 'SOHA',
          isLocked: true,
          lockedBy: sohAlignment?.lockedBy,
        });
        dispatch(
          updateProcessStatus(
            processStatus.map(status =>
              status.process === 'SOHA'
                ? {
                    ...status,
                    isLocked: true,
                    lockedBy: sohAlignment?.lockedBy,
                  }
                : status
            )
          )
        );
      }
    }

    await dispatch(unlockDeliveries());

    dispatch(initRecallState());
    dispatch(initDevices());
    dispatch(initPrints());
    dispatch(initScannedProducts());
    dispatch(initSaleProducts());
    dispatch(initCycleCountState());
    dispatch(initDeliveryProducts());
    dispatch(initConfirmDeliveryProducts());
    dispatch(initDeliveryList());
    dispatch(initStoreToStoreState());
    dispatch(initRolesState());
    dispatch(initReprice());
    dispatch(initProfilingUserState());
    dispatch(initProfilingStoresState());
    dispatch(initProfilingReasonCode());
    dispatch(initProfilingReasonCode());
    dispatch(initDamagesReturnsState());
    dispatch(initInventoryState());
    dispatch(initManageDeliveryState());
    dispatch(initPosState());
    dispatch(initBISState());
    dispatch(initExpiringSessionState());

    sessionStorage.removeItem('idCheckout');
    sessionStorage.removeItem('soha-status');
  }, [
    recall.status,
    deliveryManagedInUse,
    bisIsLocked,
    salesPosProcessIsLocked,
    returnPosProcessIsLocked,
    manualProcessLocked,
    deleteProcessLocked,
    cycleCountLocked,
    sohaLocked,
    subscribeDevice,
    setDeviceIdOnMqttClient,
    handleProcess,
    storeCode,
    username,
    dispatch,
  ]);

  return initApplicationState;
};
